import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type HazardRisk = {
  hazard: string;
  mitigation: string;
};

export type HazardsRisksFormValues = {
  hazardsRisks?: HazardRisk[];
};

const FIELD_NAME = 'hazardsRisks';

const HazardsRisks: React.FC = () => {
  const { control, register } = useFormContext<HazardsRisksFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_NAME,
  });

  return (
    <div className="mt-3 rounded border border-neutral-500 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="tw:m-0 tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
          Hazards & Risks
        </h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ hazard: '', mitigation: '' })}
        >
          <Plus data-icon="inline-start" />
          Add row
        </Button>
      </div>
      {fields.length === 0 && (
        <p className="txt--sml text-weak mb-0">No hazards recorded yet</p>
      )}
      {fields.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hazard</TableHead>
              <TableHead>Mitigation</TableHead>
              <TableHead>
                <span className="visually-hidden">Delete</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <Input
                    aria-label={`Hazard ${index + 1}`}
                    {...register(`${FIELD_NAME}.${index}.hazard`)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    aria-label={`Mitigation ${index + 1}`}
                    {...register(`${FIELD_NAME}.${index}.mitigation`)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete hazard ${index + 1}`}
                    onClick={() => remove(index)}
                  >
                    <Trash2 />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default HazardsRisks;
